import React, { useState, useEffect, useCallback } from 'react';
import { Send, Loader2 } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import TurnstileWidget, { TURNSTILE_ENABLED } from './TurnstileWidget';
import Confetti from './Confetti';
import { faqs } from '../mock';
import { toast } from 'sonner';

// Same-origin server-side endpoint. In production this is the Cloudflare
// Pages Function at functions/api/contact.js. In the Emergent preview the
// ingress routes /api/* to the FastAPI mirror. No Emergent/localhost URL and
// no Brevo key ever live in the frontend bundle.
const CONTACT_ENDPOINT = '/api/contact';

const PROJECT_NAME = 'Residenza Albero Milano';

const EMPTY = {
  nome: '',
  cognome: '',
  email: '',
  telefono: '',
  messaggio: '',
  consensoPrivacy: false,
  consensoMarketing: false,
  dataAppuntamento: '',
  slotAppuntamento: '',
};

const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [utm, setUtm] = useState({ source: '', medium: '', campaign: '', term: '', content: '' });
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [tsNonce, setTsNonce] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);

  const handleToken = useCallback((t) => setTurnstileToken(t || ''), []);

  const celebrate = useCallback(() => {
    setShowConfetti(false);
    // restart the overlay even on quick repeated submits
    requestAnimationFrame(() => setShowConfetti(true));
    setTimeout(() => setShowConfetti(false), 4000);
  }, []);

  // Capture UTM parameters from the URL (persist through the session).
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const captured = {
      source: p.get('utm_source') || '',
      medium: p.get('utm_medium') || '',
      campaign: p.get('utm_campaign') || '',
      term: p.get('utm_term') || '',
      content: p.get('utm_content') || '',
    };
    if (Object.values(captured).some(Boolean)) {
      setUtm(captured);
      try { sessionStorage.setItem('uv_utm', JSON.stringify(captured)); } catch (_) {}
    } else {
      try {
        const saved = sessionStorage.getItem('uv_utm');
        if (saved) setUtm(JSON.parse(saved));
      } catch (_) {}
    }
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();

    // (a) client-side field validation
    if (!form.nome || !form.cognome || !form.email) {
      toast.error('Compila i campi obbligatori', { description: 'Nome, cognome ed email sono richiesti.' });
      return;
    }

    if (!form.consensoPrivacy) {
      toast.error('Consenso privacy richiesto', { description: 'Devi accettare la Privacy Policy per inviare la richiesta.' });
      return;
    }

    if ((form.dataAppuntamento && !form.slotAppuntamento) || (!form.dataAppuntamento && form.slotAppuntamento)) {
      toast.error('Completa i dati dell’appuntamento', { description: 'Seleziona sia la data sia lo slot orario.' });
      return;
    }
    // (b) anti-spam token must be present when Turnstile is enabled
    if (TURNSTILE_ENABLED && !turnstileToken) {
      toast.error('Verifica antispam', { description: 'Attendi un istante e riprova.' });
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(CONTACT_ENDPOINT, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          nome: form.nome,
          cognome: form.cognome,
          email: form.email,
          telefono: form.telefono,
          messaggio: form.messaggio,
          progettoImmobiliare: PROJECT_NAME,
          consensoPrivacy: form.consensoPrivacy,
          consensoMarketing: form.consensoMarketing,
          dataAppuntamento: form.dataAppuntamento,
          slotAppuntamento: form.slotAppuntamento,
          utm,
          turnstileToken,
        }),
      });
      const data = await res.json().catch(() => ({}));

      // Granular outcomes (no technical details shown to the user)
      if (res.status === 400 || data.code === 'validation') {
        toast.error('Controlla i dati inseriti', { description: data.error || 'Alcuni campi non sono validi.' });
      } else if (res.status === 403 || data.code === 'turnstile') {
        toast.error('Verifica antispam non superata', { description: 'Riprova tra qualche istante.' });
      } else if (!res.ok || data.ok === false) {
        toast.error('Invio non riuscito', { description: 'Riprova pi\u00f9 tardi.' });
      } else if (data.emailSent === false) {
        // Lead saved but email delivery deferred (Brevo issue)
        toast.success('Richiesta ricevuta!', { description: 'Ti ricontatteremo a breve.' });
        celebrate();
        setForm(EMPTY);
      } else {
        toast.success('Messaggio inviato!', {
          description: 'Ti rispondo entro 24 ore. Controlla la tua email di conferma.',
        });
        celebrate();
        setForm(EMPTY);
      }
    } catch (err) {
      toast.error('Invio non riuscito', { description: 'Controlla la connessione e riprova.' });
    } finally {
      setLoading(false);
      setTurnstileToken('');
      setTsNonce((n) => n + 1); // reset the Turnstile widget for the next submission
    }
  };

  return (
    <section id="contatti" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 scroll-mt-20">
      {showConfetti && <Confetti />}
      <div className="grid lg:grid-cols-2 gap-12">
        {/* Form */}
        <div className="reveal">
          <span className="label-mono">// CONTATTI</span>
          <h2 className="mt-4 font-display font-semibold text-white text-3xl md:text-4xl">
            Richiedi informazioni sul progetto <span className="gold-text">immobiliare</span>
          </h2>
          <p className="mt-5 text-neutral-400 leading-relaxed">
            Che tu stia valutando quale pacchetto scegliere, voglia capire se il corso &egrave; adatto al tuo livello o
            abbia semplicemente curiosit&agrave; su come funziona la piattaforma, sono qui. Rispondo personalmente a
            ogni messaggio, di solito entro 24 ore.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label htmlFor="nome" className="text-neutral-300">Nome *</Label>
                <Input id="nome" value={form.nome} onChange={set('nome')} className="bg-[var(--uv-surface)] border-white/15 text-white h-11" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cognome" className="text-neutral-300">Cognome</Label>
                <Input id="cognome" value={form.cognome} onChange={set('cognome')} className="bg-[var(--uv-surface)] border-white/15 text-white h-11" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-neutral-300">Email *</Label>
                <Input id="email" type="email" value={form.email} onChange={set('email')} className="bg-[var(--uv-surface)] border-white/15 text-white h-11" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="telefono" className="text-neutral-300">Telefono</Label>
                <Input id="telefono" value={form.telefono} onChange={set('telefono')} placeholder="+39 ..." className="bg-[var(--uv-surface)] border-white/15 text-white h-11" />
              </div>
            </div>
            <div className="rounded-xl border border-white/10 bg-[var(--uv-surface)] p-4">
              <p className="text-xs uppercase tracking-[0.18em] text-neutral-500">Progetto immobiliare</p>
              <p className="mt-1 text-white font-medium">{PROJECT_NAME}</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="messaggio" className="text-neutral-300">Messaggio</Label>
              <Textarea id="messaggio" value={form.messaggio} onChange={set('messaggio')} rows={4}
                placeholder="Scrivi qui eventuali richieste o informazioni..."
                className="bg-[var(--uv-surface)] border-white/15 text-white resize-none" />
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label htmlFor="dataAppuntamento" className="text-neutral-300">Data appuntamento</Label>
                <Input
                  id="dataAppuntamento"
                  type="date"
                  value={form.dataAppuntamento}
                  onChange={set('dataAppuntamento')}
                  className="bg-[var(--uv-surface)] border-white/15 text-white h-11"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-neutral-300">Slot orario</Label>
                <Select value={form.slotAppuntamento} onValueChange={(v) => setForm({ ...form, slotAppuntamento: v })}>
                  <SelectTrigger className="bg-[var(--uv-surface)] border-white/15 text-white h-11">
                    <SelectValue placeholder="Seleziona uno slot" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="09:00 - 09:30">09:00 - 09:30</SelectItem>
                    <SelectItem value="11:00 - 11:30">11:00 - 11:30</SelectItem>
                    <SelectItem value="14:30 - 15:00">14:30 - 15:00</SelectItem>
                    <SelectItem value="16:30 - 17:00">16:30 - 17:00</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-3 rounded-xl border border-white/10 bg-[var(--uv-surface)] p-4">
              <label className="flex items-start gap-3 text-sm text-neutral-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.consensoPrivacy}
                  onChange={(e) => setForm({ ...form, consensoPrivacy: e.target.checked })}
                  className="mt-1"
                />
                <span>Ho letto e accetto la Privacy Policy *</span>
              </label>

              <label className="flex items-start gap-3 text-sm text-neutral-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.consensoMarketing}
                  onChange={(e) => setForm({ ...form, consensoMarketing: e.target.checked })}
                  className="mt-1"
                />
                <span>Acconsento a ricevere comunicazioni marketing.</span>
              </label>
            </div>

            {/* Invisible/interaction-only anti-spam (renders only if configured) */}
            <TurnstileWidget onToken={handleToken} nonce={tsNonce} />

            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-[var(--uv-gold)] text-[#0c0c0e] font-semibold hover:bg-[var(--uv-gold-soft)] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? <Loader2 size={17} className="animate-spin" /> : <Send size={17} />}
              {loading ? 'Invio...' : 'Invia'}
            </button>
          </form>
        </div>

        {/* FAQ */}
        <div className="reveal">
          <h2 className="font-display font-semibold text-white text-3xl md:text-4xl mb-8">
            Domande frequenti <span className="gold-text">sul corso</span>
          </h2>
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((f, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="border border-white/10 rounded-xl px-5 bg-[var(--uv-surface)] data-[state=open]:border-[var(--uv-gold)]/40"
              >
                <AccordionTrigger className="hover:no-underline text-left text-white font-medium py-4">
                  {i + 1}. {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-neutral-400 leading-relaxed pb-5">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
};

export default Contact;
